import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Mail, MailOpen, Trash2, Filter, RefreshCw, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import useConfirm from '@/hooks/use-confirm';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Replies = () => {
  const { confirm, confirmDialog } = useConfirm();

  const [replies, setReplies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' or 'unread'
  const [selectedReply, setSelectedReply] = useState(null);
  const [syncState, setSyncState] = useState(null);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    fetchData();
  }, [filter]);

  const fetchData = async () => {
    try {
      const [repliesRes, statsRes, syncRes] = await Promise.all([
        axios.get(`${API}/replies?unread_only=${filter === 'unread'}`),
        axios.get(`${API}/replies/stats`),
        axios.get(`${API}/resend/sync`)
      ]);
      setReplies(repliesRes.data);
      setStats(statsRes.data);
      setSyncState(syncRes.data);
    } catch (error) {
      console.error('Error fetching replies:', error);
      toast.error('Cevaplar yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  // Gelen cevaplar webhook'a ek olarak Resend API'sinden de çekilebilir.
  const syncFromResend = async () => {
    setSyncing(true);
    try {
      const res = await axios.post(`${API}/resend/sync`);
      const created = res.data?.received?.created ?? 0;
      toast.success(
        created > 0 ? `${created} yeni cevap alındı` : 'Yeni cevap yok — liste güncel'
      );
      await fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Resend senkronu başarısız');
    } finally {
      setSyncing(false);
    }
  };

  const markAsRead = async (replyId, read = true) => {
    try {
      await axios.put(`${API}/replies/${replyId}/mark-read`, null, {
        params: { read }
      });
      toast.success(read ? 'Okundu olarak işaretlendi' : 'Okunmadı olarak işaretlendi');
      fetchData();
    } catch (error) {
      toast.error('İşlem başarısız');
    }
  };

  const deleteReply = (replyId) => {
    confirm({
      title: 'Cevap silinsin mi?',
      description: 'Bu gelen cevap kalıcı olarak silinecek.',
      confirmLabel: 'Sil',
      destructive: true,
      onConfirm: () => doDeleteReply(replyId),
    });
  };

  const doDeleteReply = async (replyId) => {
    try {
      await axios.delete(`${API}/replies/${replyId}`);
      toast.success('Cevap silindi');
      fetchData();
      if (selectedReply?.id === replyId) {
        setSelectedReply(null);
      }
    } catch (error) {
      toast.error('Silme başarısız');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#6366f1]"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-[#0f172a] tracking-tight">
            Gelen Cevaplar 📬
          </h1>
          <p className="text-sm text-[#64748b] mt-1">
            Kampanyalarınıza gelen email cevaplarını yönetin
          </p>
        </div>
        <div className="flex items-center gap-2">
          {syncState?.configured && (
            <Button onClick={syncFromResend} variant="outline" size="sm" disabled={syncing}>
              <Download size={15} className="mr-2" />
              {syncing ? 'Alınıyor...' : "Resend'den Al"}
            </Button>
          )}
          <Button onClick={fetchData} variant="outline" size="sm">
            <RefreshCw size={15} className="mr-2" />
            Yenile
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-blue-100 text-sm">Toplam Cevap</p>
                <p className="text-2xl font-bold mt-1">{stats.total_replies}</p>
              </div>
              <Mail size={40} className="opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-orange-100 text-sm">Okunmamış</p>
                <p className="text-2xl font-bold mt-1">{stats.unread_replies}</p>
              </div>
              <MailOpen size={40} className="opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-green-100 text-sm">Cevap Oranı</p>
                <p className="text-2xl font-bold mt-1">{stats.reply_rate}%</p>
              </div>
              <RefreshCw size={40} className="opacity-80" />
            </div>
          </div>
        </div>
      )}

      {/* Filter */}
      <div className="flex items-center gap-2">
        <Filter size={20} className="text-[#64748b]" />
        <div className="flex gap-2">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => setFilter('all')}
            size="sm"
          >
            Tümü ({stats?.total_replies || 0})
          </Button>
          <Button
            variant={filter === 'unread' ? 'default' : 'outline'}
            onClick={() => setFilter('unread')}
            size="sm"
          >
            Okunmamış ({stats?.unread_replies || 0})
          </Button>
        </div>
      </div>

      {/* Replies List */}
      {replies.length === 0 ? (
        <div className="text-center py-14 bg-white rounded-xl border border-[#e2e8f0]">
          <Mail size={48} className="mx-auto text-[#cbd5e1] mb-3" />
          <h3 className="text-base font-bold text-[#0f172a] mb-1">
            {filter === 'unread' ? 'Okunmamış cevap yok' : 'Henüz cevap gelmedi'}
          </h3>
          <p className="text-sm text-[#64748b]">
            {filter === 'unread'
              ? 'Tüm cevaplar okundu olarak işaretlenmiş'
              : "Kampanyalarınıza gelen cevaplar burada görünür. Resend'de bekleyenleri çekmek için “Resend'den Al” butonunu kullanın."}
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Replies List */}
          <div className="space-y-3">
            {replies.map((reply) => (
              <div
                key={reply.id}
                onClick={() => setSelectedReply(reply)}
                className={`p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  selectedReply?.id === reply.id
                    ? 'border-[#6366f1] bg-[#f5f3ff]'
                    : reply.read
                    ? 'border-[#e2e8f0] bg-white hover:border-[#cbd5e1]'
                    : 'border-[#fbbf24] bg-[#fffbeb] hover:border-[#f59e0b]'
                }`}
              >
                <div className="flex items-start justify-between mb-2">
                  <div className="flex items-center gap-2">
                    {reply.read ? (
                      <MailOpen size={18} className="text-[#64748b]" />
                    ) : (
                      <Mail size={18} className="text-[#f59e0b]" />
                    )}
                    <span className="font-semibold text-[#0f172a]">{reply.from_email}</span>
                  </div>
                  <span className="text-xs text-[#64748b]">
                    {new Date(reply.received_at).toLocaleDateString('tr-TR')}
                  </span>
                </div>
                <p className="text-sm font-medium text-[#0f172a] mb-1">{reply.subject}</p>
                <p className="text-sm text-[#64748b] line-clamp-2">
                  {reply.body?.substring(0, 150)}...
                </p>
              </div>
            ))}
          </div>

          {/* Reply Detail */}
          {selectedReply && (
            <div className="bg-white rounded-xl border border-[#e2e8f0] p-5 sticky top-6">
              <div className="flex items-start justify-between mb-4">
                <h3 className="text-lg font-bold text-[#0f172a]">Cevap Detayı</h3>
                <div className="flex gap-2">
                  <Button
                    onClick={() => markAsRead(selectedReply.id, !selectedReply.read)}
                    variant="outline"
                    size="sm"
                  >
                    {selectedReply.read ? <Mail size={16} /> : <MailOpen size={16} />}
                  </Button>
                  <Button
                    onClick={() => deleteReply(selectedReply.id)}
                    variant="outline"
                    size="sm"
                    className="text-red-600 hover:bg-red-50"
                  >
                    <Trash2 size={16} />
                  </Button>
                </div>
              </div>

              <div className="space-y-4">
                <div>
                  <p className="text-xs text-[#64748b]">Gönderen</p>
                  <p className="font-medium text-[#0f172a]">{selectedReply.from_email}</p>
                </div>

                <div>
                  <p className="text-xs text-[#64748b]">Alıcı</p>
                  <p className="font-medium text-[#0f172a]">{selectedReply.to_email}</p>
                </div>

                <div>
                  <p className="text-xs text-[#64748b]">Konu</p>
                  <p className="font-medium text-[#0f172a]">{selectedReply.subject}</p>
                </div>

                <div>
                  <p className="text-xs text-[#64748b]">Tarih</p>
                  <p className="font-medium text-[#0f172a]">
                    {new Date(selectedReply.received_at).toLocaleString('tr-TR')}
                  </p>
                </div>

                <div>
                  <p className="text-xs text-[#64748b] mb-2">Mesaj</p>
                  <div className="bg-[#f8fafc] rounded-lg p-4 border border-[#e2e8f0] max-h-96 overflow-y-auto">
                    <p className="text-sm text-[#0f172a] whitespace-pre-wrap">
                      {selectedReply.body}
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {confirmDialog}
    </div>
  );
};

export default Replies;
